import React, { ReactElement, RefObject, useRef } from "react";
import { useDependency } from "../../../../contexts/DependencyContext";
import { useTimeMachine } from "../../../../contexts/TimeMachineContext";
import { useRecurrentAsyncCall } from "../../../../hooks/UseAsyncCall";
import { DataSourceService } from "../../../../services/http/datasource/DataSourceService";
import { IDataSource } from "../../../../services/http/datasource/models/IDataSource";
import { INeuralNetworkPredictionResult } from "../../../../services/http/measurelist/models/INeuralNetworkPredictionResult";
import { MeasureListService } from "../../../../services/http/measurelist/MeasureListService";
import { GetNeuralNetworkPredictionParameters } from "../../../../services/http/measurelist/models/GetNeuralNetworkPredictionParameters";
import { DateService } from "../../../../services/types/DateService";
import IndicatorNumber from "../../IndicatorNumber";
import LineChart, { ILineChartSeries, ChartPointsPerPixelRatio } from "../../LineChart";
import Spinner from "../../Spinner";
import NoData from "../../NoData";
import styles from './MachineLearningPrediction.module.scss';
import { TooltipItem } from "chart.js";
import { className } from "../../../../shared/helpers";

interface IMachineLearningPredictionContent
{
    dataSource: IDataSource;
    neuralNetworkPrediction: INeuralNetworkPredictionResult | null;
}

interface IMachineLearningPredictionProps
{
    dataSourceId: number;
    indicatorId: number;
}

async function loadData(dataSourceService: DataSourceService, measureListService: MeasureListService, dateService: DateService, dataSourceId: number, indicatorId: number, date: Date, chartContainerRef: RefObject<HTMLDivElement>): Promise<IMachineLearningPredictionContent>
{
    const maxPoints = chartContainerRef.current ? Math.round(chartContainerRef.current.offsetWidth * ChartPointsPerPixelRatio) : 1;
    const dataSource = await dataSourceService.getById(dataSourceId);
    const parameters = new GetNeuralNetworkPredictionParameters();
    parameters.indicatorId = indicatorId;
    parameters.toDate = date ?? new Date();
    parameters.fromDate = dateService.addDays(parameters.toDate, -1);
    parameters.maxPoints = maxPoints;

    const neuralNetworkPrediction = maxPoints > 1 ? await measureListService.getNeuralNetworkPrediction(parameters) : null;

    return { dataSource, neuralNetworkPrediction };
}

const MachineLearningPrediction = (props: IMachineLearningPredictionProps): ReactElement =>
{
    const dataSourceService = useDependency(DataSourceService);
    const measureListService = useDependency(MeasureListService);
    const dateService = useDependency(DateService);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const timeMachineDate = useTimeMachine();
    const { loading, content } = useRecurrentAsyncCall(parseInt(process.env.REACT_APP_CHART_REFRESH_RATE ?? "300000"), loadData, dataSourceService, measureListService, dateService, props.dataSourceId, props.indicatorId, timeMachineDate, chartContainerRef);

    const getChartSeries = (): ILineChartSeries[] =>
    {
        if (loading || !content || !content.dataSource || !content.neuralNetworkPrediction || !content.neuralNetworkPrediction.timestamps ||
            !content.neuralNetworkPrediction.values || !content.neuralNetworkPrediction.outputs)
                return [];

        const labels = content.neuralNetworkPrediction.timestamps.map(x => x.toLocaleString());

        const valuesSeries = {
            title: content.dataSource.name,
            values: content.neuralNetworkPrediction.values.map(x => x !== null ? x : null),
            labels
        } as ILineChartSeries;

        let accuracy = "";
        if (content?.neuralNetworkPrediction?.predictionAccuracy)
        {
            accuracy = ` (${content?.neuralNetworkPrediction?.predictionAccuracy.toFixed(2)}% Accuracy)`;
        }
        const predictionSeries = {
            title: `Prediction${accuracy}`,
            values: content.neuralNetworkPrediction.outputs.map(x => x !== null ? x : null),
            labels
        } as ILineChartSeries;

        return [valuesSeries, predictionSeries];
    }

    function getChartLabel(): string
    {
        const dataSource = content?.dataSource;

        return dataSource
            ? `${dataSource.unitOfMeasureName} ${(dataSource.unitOfMeasureId !== 41
                ? `(${dataSource.unitOfMeasureCode})`
                : dataSource.unitOfMeasureCode)}`
            : '';
    }

    const getTooltipLabel = (tooltipItem: TooltipItem<"line">): string =>
    {
        const fixedRawValue = Number(tooltipItem.raw)?.toFixed(5);
        const floatValue = parseFloat(fixedRawValue);
        return floatValue.toString();
    };

    const getTooltipTitle = (tooltipItems: TooltipItem<"line">[]): string =>
    {
        if (!content?.neuralNetworkPrediction || !tooltipItems || tooltipItems.length === 0) return '';

        const index = Number(tooltipItems[0].dataIndex);
        if (index === null || isNaN(index)) return '';

        return dateService.format(new Date(content.neuralNetworkPrediction.timestamps[index]), 'MM/dd/yyyy HH:mm:ss');
    }

    const chartSeries = getChartSeries();
    const hasValidMeasures = chartSeries && chartSeries.length > 0 && chartSeries[0].values.length > 0;
    const machineLearningNotConfigured = !content?.neuralNetworkPrediction;

    const valuesAggregations = {
        title: content?.dataSource?.name,
        titleColor: "var(--info-color)",
        indicators: [
            {
                label: "Min value",
                unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
                value: content?.neuralNetworkPrediction?.valuesAgregation?.minimum
            },
            {
                label: "Max value",
                unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
                value: content?.neuralNetworkPrediction?.valuesAgregation?.maximum
            },
            {
                label: "Average",
                unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
                value: content?.neuralNetworkPrediction?.valuesAgregation?.average
            },
            {
                label: "Standard Deviation",
                unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
                value: content?.neuralNetworkPrediction?.valuesAgregation?.standardDeviation
            }
        ]
    };

    const outputsAgregation = {
        title: "Prediction",
        titleColor: "#f09c5c",
        indicators: [
            {
                label: "Min value",
                unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
                value: content?.neuralNetworkPrediction?.outputsAgregation?.minimum
            },
            {
                label: "Max value",
                unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
                value: content?.neuralNetworkPrediction?.outputsAgregation?.maximum
            },
            {
                label: "Average",
                unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
                value: content?.neuralNetworkPrediction?.outputsAgregation?.average
            },
            {
                label: "Standard Deviation",
                unitOfMeasure: content?.dataSource?.unitOfMeasureCode,
                value: content?.neuralNetworkPrediction?.outputsAgregation?.standardDeviation
            }
        ]
    }

    return (
        <>
            {hasValidMeasures &&
                <div className={styles.indicatorsWrapper}>
                    {valuesAggregations.indicators.some(i => typeof i.value === "number" && i.unitOfMeasure !== undefined) && (
                        <p className={styles.aggregationTitle} style={{ color: valuesAggregations.titleColor }}>{valuesAggregations.title}</p>
                    )}
                    <div {...className(styles.indicators, styles.valuesAgregations)}>
                        {valuesAggregations.indicators.map((indicator, index) =>
                        {
                            return typeof indicator.value === "number" && indicator.unitOfMeasure !== undefined && (
                                <IndicatorNumber
                                    key={index}
                                    label={indicator.label}
                                    unitOfMeasure={indicator.unitOfMeasure}
                                    value={indicator.value}
                                    decimalPlaces={2}
                                />
                            )
                        })}
                    </div>

                    {outputsAgregation.indicators.some(i => typeof i.value === "number" && i.unitOfMeasure !== undefined) && (
                        <p className={styles.aggregationTitle} style={{ color: outputsAgregation.titleColor }}>{outputsAgregation.title}</p>
                    )}
                    <div {...className(styles.indicators, styles.outputsAgregation)}>
                        {outputsAgregation.indicators.map((indicator, index) =>
                        {
                            return typeof indicator.value === "number" && indicator.unitOfMeasure !== undefined && (
                                <IndicatorNumber
                                    key={index}
                                    label={indicator.label}
                                    unitOfMeasure={indicator.unitOfMeasure}
                                    value={indicator.value}
                                    decimalPlaces={2}
                                />
                            )
                        })}
                    </div>
                </div>
            }

            <div ref={chartContainerRef}>
                {loading
                    ? <Spinner caption="Loading data..." />
                    : hasValidMeasures
                        ? <LineChart
                            series={chartSeries}
                            yLabel={getChartLabel()}
                            getTooltipLabel={getTooltipLabel}
                            getTooltipTitle={getTooltipTitle}
                            timeUnit="minute"
                            enableZoom={false}
                            displayLegend
                            size="small"
                        />
                        : machineLearningNotConfigured
                            ? <NoData badgeText="Not configured" message="Neural network is not configured." />
                            : <NoData badgeText="No measures" message={`No measures received at ${dateService.format(timeMachineDate ?? new Date(), 'MM/dd/yyyy hh:mm:ss')}`} />}
            </div>
        </>);
}

export default MachineLearningPrediction;